import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { CustomerUser, UserModule, Company } from '../../../../models/customer-data.model';
import { CustomerManagerService } from '../../../../services/customer-manager.service';

@Component({
  selector: 'app-customer-user-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule
  ],
  templateUrl: './customer-user-management.component.html',
  styleUrl: './customer-user-management.component.scss'
})
export class CustomerUserManagementComponent implements OnInit, OnDestroy {

  // 資料與狀態
  users: CustomerUser[] = [];
  filteredUsers: CustomerUser[] = [];
  selectedUser: CustomerUser | null = null;
  isEditing = false;
  isLoading = false;
  showModal = false;

  // 公司相關資料
  availableCompanies: Company[] = [];
  selectedCompanyId = '';

  // 表單
  userForm: FormGroup;

  // 搜尋與分頁
  searchTerm = '';
  currentPage = 1;
  itemsPerPage = 10;
  totalItems = 0;

  // 搜尋防抖相關
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  // 可用的權限選項
  availablePermissions = [
    { label: '超級管理者', value: 'super_admin' },
    { label: '管理者', value: 'admin' },
    { label: '使用者', value: 'user' }
  ];

  // 可用的助手列表
  availableAssistants: { assistantNo: string; title: string }[] = [];
  isLoadingAssistants = false;
  assistantsLoadError = false;

  constructor(
    private fb: FormBuilder,
    private customerManagerService: CustomerManagerService
  ) {
    this.userForm = this.createForm();
  }

  ngOnInit(): void {
    this.loadCompanies();
    this.loadUsers();
    this.setupSearchDebounce();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * 設定搜尋防抖功能
   */
  private setupSearchDebounce(): void {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe((searchTerm: string) => {
      this.searchTerm = searchTerm;
      this.applyFilters();
    });
  }

  /**
   * 建立表單
   */
  createForm(): FormGroup {
    return this.fb.group({
      companyid: ['', [Validators.required]],
      username: ['', [Validators.required, Validators.minLength(3)]],
      password: ['', this.isEditing ? [] : [Validators.required, Validators.minLength(6)]],
      email: ['', [Validators.required, Validators.email]],
      modules: this.fb.array([]),
      mfaEnabled: [false]
    });
  }

  /**
   * 獲取 modules FormArray
   */
  get modulesFormArray(): FormArray {
    return this.userForm.get('modules') as FormArray;
  }

  /**
   * 載入使用者資料
   */
  loadUsers(): void {
    this.isLoading = true;

    this.customerManagerService.getCustomerUsers(this.searchTerm, this.selectedCompanyId || undefined, this.currentPage, this.itemsPerPage)
      .subscribe({
        next: (response) => {
          this.users = response.data;
          this.filteredUsers = response.data;
          this.totalItems = response.total;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('載入使用者資料時發生錯誤:', error);
          this.isLoading = false;
          alert('載入使用者資料失敗: ' + error);
        }
      });
  }

  /**
   * 載入公司列表
   */
  loadCompanies(): void {
    this.customerManagerService.getCompanies()
      .subscribe({
        next: (companies) => {
          this.availableCompanies = companies;
          // 由於 API 已經包含 userCount，不需要再次載入統計資料
          console.log('成功載入公司列表:', companies);
        },
        error: (error) => {
          console.error('載入公司列表時發生錯誤:', error);
          // 如果無法載入公司列表，使用空陣列
          this.availableCompanies = [];
        }
      });
  }

  /**
   * 載入指定公司的助手列表
   */
  loadCompanyAssistants(companyId: string): void {
    if (!companyId) {
      this.availableAssistants = [];
      this.assistantsLoadError = false;
      return;
    }

    this.isLoadingAssistants = true;
    this.assistantsLoadError = false;

    this.customerManagerService.getCompanyAssistants(companyId)
      .subscribe({
        next: (assistants) => {
          // 在助手列表前面加入 "ALL" 選項
          this.availableAssistants = [
            { assistantNo: 'ALL', title: '所有助手' },
            ...assistants
          ];
          this.isLoadingAssistants = false;
          this.assistantsLoadError = false;
          console.log('成功載入公司助手列表:', this.availableAssistants);
        },
        error: (error) => {
          console.error('載入公司助手時發生錯誤:', error);
          this.availableAssistants = [];
          this.isLoadingAssistants = false;
          this.assistantsLoadError = true;
        }
      });
  }

  /**
   * 載入各公司的使用者數量
   * 注意：此方法現在不再需要，因為 getCompanies() 已經包含 userCount
   * 保留此方法以維持向後相容性
   */
  loadCompanyUserCounts(): void {
    // 由於 getCompanies() API 已經包含 userCount，此方法現在是多餘的
    // 但為了避免破壞現有功能，暫時保留
    console.log('使用者統計已包含在公司列表中，跳過額外載入');
  }

  /**
   * 套用搜尋篩選
   */
  applyFilters(): void {
    this.currentPage = 1;
    this.loadUsers();
  }

  /**
   * 獲取當前頁面的項目
   */
  getCurrentPageItems(): CustomerUser[] {
    // 由於我們現在使用後端分頁，直接返回已篩選的資料
    return this.filteredUsers;
  }

  /**
   * 獲取總頁數
   */
  getTotalPages(): number {
    return Math.ceil(this.totalItems / this.itemsPerPage);
  }

  /**
   * 新增使用者
   */
  addUser(): void {
    this.selectedUser = null;
    this.isEditing = false;

    // 重新建立表單，讓密碼欄位在新增模式下是必填
    this.userForm = this.createForm();

    this.userForm.reset({
      mfaEnabled: false
    });
    this.clearModulesFormArray();
    this.addModuleFormGroup();
    // 清空助手列表，等待選擇公司後載入
    this.availableAssistants = [];
    // 在新增模式下確保公司選擇是啟用的
    this.userForm.get('companyid')?.enable();
    this.showModal = true;
  }

  /**
   * 編輯使用者
   */
  editUser(user: CustomerUser): void {
    this.selectedUser = user;
    this.isEditing = true;

    // 重新建立表單，讓密碼欄位在編輯模式下不是必填
    this.userForm = this.createForm();

    this.populateForm(user);
    // 載入該使用者公司的助手列表
    this.loadCompanyAssistants(user.companyid);
    // 在編輯模式下禁用公司選擇
    this.userForm.get('companyid')?.disable();
    this.showModal = true;
  }

  /**
   * 填充表單資料
   */
  populateForm(user: CustomerUser): void {
    this.userForm.patchValue({
      companyid: user.companyid,
      username: user.username,
      password: '', // 編輯模式下密碼欄位顯示為空白
      email: user.email,
      mfaEnabled: user.mfa?.enabled || false
    });

    // 填充 modules
    this.clearModulesFormArray();
    user.modules.forEach(module => {
      this.addModuleFormGroup(module);
    });
  }

  /**
   * 清空 modules FormArray
   */
  clearModulesFormArray(): void {
    while (this.modulesFormArray.length !== 0) {
      this.modulesFormArray.removeAt(0);
    }
  }

  /**
   * 新增 module FormGroup
   */
  addModuleFormGroup(module?: UserModule): void {
    const moduleGroup = this.fb.group({
      assistantNo: [module?.assistantNo || '', Validators.required],
      permission: [module?.permission || 'user', Validators.required]  // 預設使用 'user' 權限值
    });

    this.modulesFormArray.push(moduleGroup);
  }

  /**
   * 移除 module FormGroup
   */
  removeModuleFormGroup(index: number): void {
    this.modulesFormArray.removeAt(index);
  }

  /**
   * 儲存使用者
   */
  saveUser(): void {
    if (this.userForm.valid && this.modulesFormArray.length > 0) {
      const formValue = this.userForm.getRawValue(); // 使用 getRawValue() 來取得包含 disabled 欄位的值

      const userData: Omit<CustomerUser, 'id' | 'createdAt' | 'updatedAt'> = {
        companyid: formValue.companyid,
        username: formValue.username,
        password: formValue.password,
        email: formValue.email,
        modules: formValue.modules,
        mfa: {
          enabled: formValue.mfaEnabled,
          setupAt: formValue.mfaEnabled ? new Date().toISOString() : undefined
        }
      };

      // 在編輯模式下，如果密碼為空，則保留原始密碼
      if (this.isEditing && this.selectedUser?.id) {
        if (!formValue.password || formValue.password.trim() === '') {
          // 保留原始密碼
          userData.password = this.selectedUser.password;
        }

        // 更新現有使用者
        this.customerManagerService.updateCustomerUser(this.selectedUser.id, userData)
          .subscribe({
            next: (updatedUser) => {
              this.loadUsers();
              this.closeModal();
              alert('使用者更新成功！');
            },
            error: (error) => {
              console.error('更新使用者時發生錯誤:', error);
              alert('使用者更新失敗: ' + error);
            }
          });
      } else {
        // 新增使用者
        this.customerManagerService.createCustomerUser(userData)
          .subscribe({
            next: (newUser) => {
              this.loadUsers();
              this.closeModal();
              alert('使用者新增成功！');
            },
            error: (error) => {
              console.error('新增使用者時發生錯誤:', error);
              if (error.includes('已存在')) {
                alert('此使用者名稱已存在於該公司中');
              } else {
                alert('使用者新增失敗: ' + error);
              }
            }
          });
      }
    } else {
      if (this.modulesFormArray.length === 0) {
        alert('請至少新增一個模組權限');
      } else {
        alert('請填寫所有必要欄位');
      }
    }
  }

  /**
   * 刪除使用者
   */
  deleteUser(user: CustomerUser): void {
    if (confirm(`確定要刪除使用者 "${user.username}" 嗎？`)) {
      if (user.id) {
        this.customerManagerService.deleteCustomerUser(user.id)
          .subscribe({
            next: () => {
              this.loadUsers();
              alert('使用者刪除成功！');
            },
            error: (error) => {
              console.error('刪除使用者時發生錯誤:', error);
              alert('使用者刪除失敗: ' + error);
            }
          });
      }
    }
  }

  /**
   * 關閉模態框
   */
  closeModal(): void {
    this.showModal = false;
    this.selectedUser = null;
    this.isEditing = false;
    this.userForm.reset();
    this.clearModulesFormArray();
    // 重置公司欄位狀態
    this.userForm.get('companyid')?.enable();
  }

  /**
   * 生成ID
   */
  generateId(): string {
    return Date.now().toString();
  }

  /**
   * 搜尋變更處理（使用防抖）
   */
  onSearchChange(): void {
    this.searchSubject.next(this.searchTerm);
  }

  /**
   * 頁面變更處理
   */
  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadUsers();
  }

  /**
   * 處理公司選擇變更
   */
  onCompanyChange(): void {
    this.currentPage = 1; // 重置到第一頁
    this.loadUsers(); // 重新載入使用者資料
  }

  /**
   * 處理模態框中公司選擇變更
   */
  onModalCompanyChange(): void {
    const selectedCompanyId = this.userForm.get('companyid')?.value;
    if (selectedCompanyId) {
      // 載入該公司的助手列表
      this.loadCompanyAssistants(selectedCompanyId);

      // 清空現有的模組權限設定，因為助手可能已經改變
      this.clearModulesFormArray();
      this.addModuleFormGroup();
    } else {
      // 如果未選擇公司，清空助手列表
      this.availableAssistants = [];
      this.clearModulesFormArray();
      this.addModuleFormGroup();
    }
  }

  /**
   * 檢查是否有 "ALL" 助手被選中
   */
  hasAllAssistantSelected(): boolean {
    return this.modulesFormArray.controls.some(control =>
      control.get('assistantNo')?.value === 'ALL'
    );
  }

  /**
   * 獲取助手名稱
   */
  getAssistantTitle(assistantNo: string): string {
    const assistant = this.availableAssistants.find(a => a.assistantNo === assistantNo);
    return assistant ? assistant.title : assistantNo;
  }

  /**
   * 獲取權限顯示文字
   */
  getPermissionText(permission: string | { label: string, value: string }): string {
    // 如果傳入的是權限物件，返回 label
    if (typeof permission === 'object' && permission.label) {
      return permission.label;
    }

    // 如果傳入的是字串值，查找對應的 label
    if (typeof permission === 'string') {
      const permissionObj = this.availablePermissions.find(p => p.value === permission);
      if (permissionObj) {
        return permissionObj.label;
      }

      // 向後相容性支援舊的權限值
      switch (permission) {
        case 'admin': return '管理者';
        case 'user': return '使用者';
        case 'readonly': return '唯讀';
        default: return permission;
      }
    }

    return String(permission);
  }

  /**
   * 獲取使用者權限摘要
   */
  getUserPermissionSummary(modules: UserModule[]): string {
    if (modules.some(m => m.assistantNo === 'ALL')) {
      const allModule = modules.find(m => m.assistantNo === 'ALL');
      return `全部 (${this.getPermissionText(allModule!.permission)})`;
    }
    return `${modules.length} 個模組`;
  }
}
